import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Nav, Tab } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { fetchCaseSummary, fetchHeaderSummary } from "../api/case";
import { addClickRecord, getCaseId, getClientId, getToken } from "../Utils/helper";
import { fetchAllPages, fetchCurrentCase, setCaseSummary } from "../Redux/caseData/caseDataSlice";
import { setHeaderSummary } from "../Redux/header/headerDataSlice";
import { api_without_cancellation } from "../api/api";
import "./edit-case-type-date-modal.css";
import incidentIcon from "../assets/images/incident.svg";

import UserChatBubble from "./Forms/FirmUserBtn";

export default function EditCaseTypeDateModal({
  show,
  handleClose,
  intakeCase,
  onEnterCase,
  isEnteringCase = false,
}) {
  const dispatch = useDispatch();
  const currentCaseSummary = useSelector((state) => state?.caseData?.summary);
  const isIntakePreview = Boolean(intakeCase);
  const caseSummary = isIntakePreview
    ? {
        case_type: { id: intakeCase.case_type_id, name: intakeCase.case_type },
        incident_date: intakeCase.incident_date,
        incident_date_iso: intakeCase.incident_date_iso,
        jurisdiction_timezone: intakeCase.jurisdiction_timezone,
        intake_date: intakeCase.initial_intake_date,
        retained: intakeCase.retained_date,
        accepted_date: intakeCase.accepted_date,
        case_dropped: intakeCase.dropped_date,
        case_referred: intakeCase.referred_date,
        case_rejected: intakeCase.rejected_date,
        date_closed: intakeCase.date_closed,
      }
    : currentCaseSummary;
  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [timezones, setTimezones] = useState({ default: "", us: [], all: [] });
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [categoryDropdown, setCategoryDropdown] = useState(false);
  const [timezoneDropdown, setTimezoneDropdown] = useState(false);
  const categoryRef = useRef(null);
  const timezoneRef = useRef(null);
  const intakeCaseType = isIntakePreview
    ? categories
        .flatMap((category) => category.case_types || [])
        .find((type) =>
          intakeCase.case_type_id !== undefined && intakeCase.case_type_id !== null
            ? String(type.id) === String(intakeCase.case_type_id)
            : type.name === intakeCase.case_type
        )
    : null;
  const currentCaseTypeId = isIntakePreview
    ? intakeCaseType?.id || ""
    : caseSummary?.case_type?.id || "";
  const formik = useFormik({
    initialValues: {
      caseTypes: {
        case_type: currentCaseTypeId,
        // case_type_sol_check: false,
        // delete_sol_check: false,
      },
      caseDates: {
        intake_date: (caseSummary?.intake_date || "").slice(0, 10),
        retained: (caseSummary?.retained || "").slice(0, 10),
        accepted_date: (caseSummary?.accepted_date || "").slice(0, 10),
        date_closed: (caseSummary?.date_closed || "").slice(0, 10),
        case_dropped: (caseSummary?.case_dropped || "").slice(0, 10),
        case_referred: (caseSummary?.case_referred || "").slice(0, 10),
        case_rejected: (caseSummary?.case_rejected || "").slice(0, 10),
        doi_date: caseSummary?.incident_date_iso || "",
        jurisdiction_timezone: caseSummary?.jurisdiction_timezone || timezones.default || "",
      },
    },
    onSubmit: async (values) => {
      if (isIntakePreview) return;
      const caseTypeData = {
        case_type: values.caseTypes.case_type,
      };

      const caseDateData = {
        intake_date: values.caseDates.intake_date,
        retained: values.caseDates.retained,
        accepted_date: values.caseDates.accepted_date,
        date_closed: values.caseDates.date_closed,
        doi_date: values.caseDates.doi_date,
        case_dropped: values.caseDates.case_dropped,
        case_referred: values.caseDates.case_referred,
        case_rejected: values.caseDates.case_rejected,
        jurisdiction_timezone: values.caseDates.jurisdiction_timezone,
      };
      const caseTypeChanged = values.caseTypes.case_type !== caseSummary?.case_type?.id;
      try {
        setIsLoading(true);
        addClickRecord(999, 1);
        await api_without_cancellation.put(
          `/api/cases/${getClientId()}/${getCaseId()}/summary/?tab=case_dates`,
          caseDateData
        );
        await api_without_cancellation.put(
          `/api/cases/${getClientId()}/${getCaseId()}/summary/?tab=case_type`,
          caseTypeData
        );
        fetchHeaderSummary(getClientId(), getCaseId())
          .then((data) => {
            dispatch(setHeaderSummary(data));
          })
          .catch(() => {});
        dispatch(fetchAllPages(getCaseId(), true));
        dispatch(fetchCurrentCase(getClientId(), getCaseId()));
        fetchCaseSummary(getClientId(), getCaseId())
          .then((data) => {
            dispatch(setCaseSummary(data));
          })
          .catch((err) => {
            console.error("Error occurred", err);
          })
          .finally(() => {
            setIsLoading(false);
            // document.getElementById("edit-case-dates-modal").click();
            handleClose();
            if (caseTypeChanged) navigate("/search");
          });
      } catch (error) {
        setIsLoading(false);
        console.error("Error occurred", error);
      }
    },
    enableReinitialize: true,
  });
  const caseTypeRef = useRef(null);
  const [_caseTypeDropdown, setCaseTypeDropdown] = useState(false);
  const token = getToken();
  const filteredCaseTypes = selectedCategoryId
    ? categories.find((c) => c.id === selectedCategoryId)?.case_types || []
    : [];

  useEffect(() => {
    if (!token) return;
    api_without_cancellation
      .get("/api/case-type-categories/")
      .then((res) => {
        const data = res.data?.data || [];
        setCategories(data);
      })
      .catch((err) => console.error("Error fetching case type categories", err));
  }, [token]);

  useEffect(() => {
    if (!token || isIntakePreview) return;
    api_without_cancellation
      .get("/api/general/timezones/")
      .then((res) => {
        setTimezones({
          default: res.data?.default || "",
          us: res.data?.us || [],
          all: res.data?.all || [],
        });
      })
      .catch((err) => console.error("Error fetching timezones", err));
  }, [token, isIntakePreview]);

  useEffect(() => {
    const category = categories.find((item) =>
      item.case_types?.some((type) => type.id === currentCaseTypeId)
    );
    setSelectedCategoryId(category?.id ?? null);
    setCategoryDropdown(false);
  }, [categories, currentCaseTypeId]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (caseTypeRef.current && !caseTypeRef.current.contains(event.target)) {
        setCaseTypeDropdown(false);
      }
      if (categoryRef.current && !categoryRef.current.contains(event.target)) {
        setCategoryDropdown(false);
      }
      if (timezoneRef.current && !timezoneRef.current.contains(event.target)) {
        setTimezoneDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [caseTypeRef, categoryRef, timezoneRef]);

  return (
    <Modal
      show={show}
      onHide={isEnteringCase ? undefined : handleClose}
      dialogClassName="max-500 modal-dialog-centered "
      contentClassName="custom-modal-new-provider"
    >
      {/* <div className="modal-dialog modal-lg modal-dialog-centered Add-Client-W500" role="document"> */}
      <div className="">
        <div
          className="modal-header p-0 height-25 d-flex align-items-center justify-content-center text-center"
          style={{
            background: "var(--primary)",
          }}
        >
          <h5
            className="modal-title d-flex align-items-center justify-content-center height-25 "
            style={{
              fontSize: "14px",
              color: "white",
            }}
          >
            Case Type and Dates
          </h5>
        </div>

        <div
          className="modal-body pt-0 px-0 pb-0 min-h-400 max-h-400 overflow-hidden"
          style={{
            minHeight: "280px",
          }}
        >
          {/*  */}
          <div className="custom-tab m-t-5">
            <Tab.Container defaultActiveKey={"importantDates"}>
              <Nav
                variant="tabs"
                className="justify-content-around"
                style={{ marginTop: "5px", marginBottom: "5px" }}
              >
                <Nav.Link
                  className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite apply-before"
                  eventKey="importantDates"
                >
                  Important Dates
                </Nav.Link>
                <Nav.Link
                  className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite apply-after"
                  eventKey="caseType"
                >
                  Case Type
                </Nav.Link>
              </Nav>
              {/* <div className="" id="nav-tabContent"> */}
              <Tab.Content>
                <Tab.Pane
                  eventKey="caseType"
                  style={{
                    minHeight: "270px",
                    scrollbarWidth: "none",
                    padding: "0 5px",
                  }}
                >
                  <form id="edit_case_type_form">
                    <div
                      ref={categoryRef}
                      className="col-md-12 p-0 custom-select-new-provider m-b-5"
                    >
                      <div
                        className="dropdown-button form-control pt-0 pb-0 pe-0 rounded-0 height-25 d-flex align-items-center"
                        onClick={
                          isIntakePreview ? undefined : () => setCategoryDropdown((prev) => !prev)
                        }
                        style={{ paddingLeft: "5px", backgroundColor: "white" }}
                      >
                        <span
                          style={{
                            color: "black",
                            fontWeight: "700",
                            fontSize: "16px",
                            cursor: isIntakePreview ? "default" : "pointer",
                          }}
                        >
                          {selectedCategoryId
                            ? categories.find((c) => c.id === selectedCategoryId)?.name
                            : "Select Category"}
                        </span>
                      </div>
                      {!isIntakePreview && categoryDropdown && (
                        <div
                          className="dropdown-menu pt-0 pb-0 mt-0"
                          style={{
                            width: "100%",
                            border: "1px solid #ced4da",
                            scrollbarWidth: "none",
                            maxHeight: "200px",
                            fontSize: "16px",
                            overflowY: "auto",
                          }}
                        >
                          {categories.map((cat) => (
                            <div
                              key={cat.id}
                              className="dropdown-option bg-speciality d-flex p-l-5 align-items-center height-25"
                              style={{
                                color: "black",
                                fontWeight: "700",
                                fontSize: "16px",
                                cursor: isIntakePreview ? "default" : "pointer",
                              }}
                              onClick={() => {
                                setSelectedCategoryId(cat.id);
                                setCategoryDropdown(false);
                              }}
                            >
                              {cat.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div ref={caseTypeRef} className="col-md-12 p-0">
                      <div
                        className=""
                        style={{
                          overflowY: "auto",
                          scrollbarWidth: "none",
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
                          backgroundColor: "white",
                          opacity: selectedCategoryId ? 1 : 0.6,
                          pointerEvents: selectedCategoryId ? "auto" : "none",
                        }}
                      >
                        {filteredCaseTypes.map((type) => (
                          <label
                            key={type.id}
                            className="d-flex p-l-5 align-items-center height-25 bg-speciality"
                            style={{
                              color: "black",
                              fontWeight: "700",
                              fontSize: "14px",
                              cursor: isIntakePreview
                                ? "default"
                                : selectedCategoryId
                                  ? "pointer"
                                  : "not-allowed",
                              margin: 0,
                            }}
                          >
                            <input
                              type="radio"
                              name="caseType"
                              value={type.id}
                              checked={formik.values.caseTypes.case_type === type.id}
                              onChange={() => {
                                if (!isIntakePreview)
                                  formik.setFieldValue("caseTypes.case_type", type.id);
                              }}
                              disabled={isIntakePreview || !selectedCategoryId}
                              style={{ marginRight: "8px" }}
                            />
                            <span className="d-flex align-items-center">
                              {type.casetype_icon && (
                                <img className="ic ic-15 m-r-5" src={type.casetype_icon} />
                              )}
                              {type.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </form>
                </Tab.Pane>

                <Tab.Pane eventKey="importantDates" mountOnEnter>
                  <form id="edit_case_date_form">
                    {!isIntakePreview && (
                      <div className="d-flex mx-auto align-items-center mb-3 m-b-5">
                        <div className="col-md-3 p-l-5 pe-0 text-start">
                          <label
                            className="d-inline-block m-0 height-25 d-flex align-items-center"
                            style={{
                              color: "var(--primary-25)",
                              fontWeight: "600",
                            }}
                            htmlFor="jurisdiction_timezone"
                          >
                            Timezone
                          </label>
                        </div>
                        <div
                          ref={timezoneRef}
                          className="flex-g-1 p-l-5 p-r-5 position-relative custom-select-new-provider"
                        >
                          <button
                            type="button"
                            id="jurisdiction_timezone"
                            className="dropdown-button form-control height-25 p-0 p-l-5 p-r-5 rounded-0 d-flex align-items-center"
                            onClick={() => setTimezoneDropdown((open) => !open)}
                          >
                            {timezones.us.find(
                              (timezone) =>
                                timezone.name === formik.values.caseDates.jurisdiction_timezone
                            )?.label ||
                              formik.values.caseDates.jurisdiction_timezone ||
                              "Select timezone"}
                          </button>
                          {timezoneDropdown && (
                            <div
                              className="dropdown-menu pt-0 pb-0 mt-0"
                              style={{
                                width: "calc(100% - 10px)",
                                left: "5px",
                                border: "1px solid #ced4da",
                                maxHeight: "200px",
                                overflowY: "auto",
                                scrollbarWidth: "none",
                                zIndex: 1056,
                              }}
                            >
                              <button
                                type="button"
                                className="dropdown-option bg-speciality d-flex p-l-5 align-items-center height-25 w-100 border-0"
                                onClick={() => {
                                  formik.setFieldValue("caseDates.jurisdiction_timezone", "");
                                  setTimezoneDropdown(false);
                                }}
                              >
                                Select timezone
                              </button>
                              {timezones.us.map((timezone) => (
                                <button
                                  type="button"
                                  key={`us-${timezone.name}`}
                                  className="dropdown-option bg-speciality d-flex p-l-5 align-items-center height-25 w-100 border-0"
                                  onClick={() => {
                                    formik.setFieldValue(
                                      "caseDates.jurisdiction_timezone",
                                      timezone.name
                                    );
                                    setTimezoneDropdown(false);
                                  }}
                                >
                                  {timezone.label}
                                </button>
                              ))}
                              {timezones.all.map((timezone) => {
                                const name =
                                  typeof timezone === "string" ? timezone : timezone.name;
                                return (
                                  <button
                                    type="button"
                                    key={`all-${name}`}
                                    className="dropdown-option bg-speciality d-flex p-l-5 align-items-center height-25 w-100 border-0"
                                    onClick={() => {
                                      formik.setFieldValue("caseDates.jurisdiction_timezone", name);
                                      setTimezoneDropdown(false);
                                    }}
                                  >
                                    {name}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="d-flex align-items-center mb-3 m-b-5 mx-auto">
                      <div className="col-md-3 text-start p-l-5 pe-0">
                        <div className="height-25 d-flex align-items-center">
                          <img className="ic ic-15 m-r-5" src={incidentIcon} />
                          <label
                            className="d-inline-block m-0 "
                            style={{
                              color: "var(--primary-25)",
                              fontWeight: "600",
                            }}
                            htmlFor="doi_date"
                          >
                            Incident
                          </label>
                        </div>
                      </div>
                      <div className="flex-g-1 p-l-5 p-r-5">
                        <input
                          type="date"
                          readOnly={isIntakePreview}
                          disabled={isIntakePreview}
                          id="doi_date"
                          className="form-control height-25 p-0 p-l-5 p-r-5"
                          min="1000-01-01"
                          max="9999-12-31"
                          name="caseDates.doi_date"
                          value={formik.values.caseDates.doi_date}
                          onChange={formik.handleChange}
                        />
                      </div>
                      {/* {caseSummary?.incident_date_user && ( */}
                      {!isIntakePreview && (
                        <div className="p-r-5 p-l-5 d-flex align-items-center">
                          <UserChatBubble
                            userId={caseSummary?.incident_date_user?.id}
                            first_name={caseSummary?.incident_date_user?.first_name ?? ""}
                            last_name={caseSummary?.incident_date_user?.last_name ?? ""}
                            profilePic={caseSummary?.incident_date_user?.profile_pic ?? ""}
                            is_active={caseSummary?.incident_date_user?.is_active}
                            width={21}
                            isActive={
                              userOnlineStatus?.[caseSummary?.incident_date_user?.id]?.isOnline ??
                              false
                            }
                          />
                        </div>
                      )}
                      {/* )} */}
                    </div>

                    <div className="d-flex mx-auto align-items-center mb-3 m-b-5">
                      <div className="col-md-3 p-l-5 pe-0 text-start">
                        <label
                          className="d-inline-block m-0 height-25 "
                          style={{
                            color: "var(--primary-25)",
                            fontWeight: "600",
                          }}
                          htmlFor="intake_date"
                        >
                          Lead to Intake
                        </label>
                      </div>
                      <div className="flex-g-1 p-l-5 p-r-5">
                        <input
                          type="date"
                          readOnly={isIntakePreview}
                          disabled={isIntakePreview}
                          id="intake_date"
                          className="form-control height-25 p-0 p-l-5 p-r-5"
                          min="1000-01-01"
                          max="9999-12-31"
                          name="caseDates.intake_date"
                          value={formik.values.caseDates.intake_date}
                          onChange={formik.handleChange}
                        />
                      </div>
                      {/* {caseSummary?.intake_date_user && ( */}
                      {!isIntakePreview && (
                        <div className="p-r-5 p-l-5 d-flex align-items-center">
                          <UserChatBubble
                            userId={caseSummary?.intake_date_user?.id}
                            first_name={caseSummary?.intake_date_user?.first_name ?? ""}
                            last_name={caseSummary?.intake_date_user?.last_name ?? ""}
                            profilePic={caseSummary?.intake_date_user?.profile_pic ?? ""}
                            is_active={caseSummary?.intake_date_user?.is_active}
                            width={21}
                            isActive={
                              userOnlineStatus?.[caseSummary?.intake_date_user?.id]?.isOnline ??
                              false
                            }
                          />
                        </div>
                      )}
                    </div>
                    <div className="d-flex mx-auto m-b-5 align-items-center mb-3">
                      <div className="col-md-3 p-l-5 pe-0 text-start">
                        <label
                          className="d-inline-block m-0 height-25"
                          style={{
                            color: "var(--primary-25)",
                            fontWeight: "600",
                          }}
                          htmlFor="retained"
                        >
                          Retained
                        </label>
                      </div>
                      <div className="flex-g-1 p-l-5 p-r-5">
                        <input
                          type="date"
                          readOnly={isIntakePreview}
                          disabled={isIntakePreview}
                          id="retained"
                          className="form-control  height-25 p-0 p-l-5 p-r-5"
                          min="1000-01-01"
                          max="9999-12-31"
                          name="caseDates.retained"
                          value={formik.values.caseDates.retained}
                          onChange={formik.handleChange}
                        />
                      </div>
                      {/* {caseSummary?.retained_date_user && ( */}
                      {!isIntakePreview && (
                        <div className="p-r-5 p-l-5 d-flex align-items-center">
                          <UserChatBubble
                            userId={caseSummary?.retained_date_user?.id}
                            first_name={caseSummary?.retained_date_user?.first_name ?? ""}
                            last_name={caseSummary?.retained_date_user?.last_name ?? ""}
                            profilePic={caseSummary?.retained_date_user?.profile_pic ?? ""}
                            is_active={caseSummary?.retained_date_user?.is_active}
                            width={21}
                            isActive={
                              userOnlineStatus?.[caseSummary?.retained_date_user?.id]?.isOnline ??
                              false
                            }
                          />
                        </div>
                      )}
                    </div>
                    <div className="d-flex align-items-center mb-3 mx-auto m-b-5">
                      <div className="col-md-3 p-l-5 pe-0 text-start">
                        <label
                          className="d-inline-block m-0 height-25"
                          style={{
                            color: "var(--primary-25)",
                            fontWeight: "600",
                          }}
                          htmlFor="accepted_date"
                        >
                          Accepted
                        </label>
                      </div>
                      <div className="flex-g-1 p-l-5 p-r-5">
                        <input
                          type="date"
                          readOnly={isIntakePreview}
                          disabled={isIntakePreview}
                          id="accepted_date"
                          className="form-control height-25 p-0 p-l-5 p-r-5"
                          min="1000-01-01"
                          max="9999-12-31"
                          name="caseDates.accepted_date"
                          value={formik.values.caseDates.accepted_date}
                          onChange={formik.handleChange}
                        />
                      </div>
                      {/* {caseSummary?.accepted_date_user && ( */}
                      {!isIntakePreview && (
                        <div className="p-r-5 p-l-5 d-flex align-items-center">
                          <UserChatBubble
                            userId={caseSummary?.accepted_date_user?.id}
                            first_name={caseSummary?.accepted_date_user?.first_name ?? ""}
                            last_name={caseSummary?.accepted_date_user?.last_name ?? ""}
                            profilePic={caseSummary?.accepted_date_user?.profile_pic ?? ""}
                            is_active={caseSummary?.accepted_date_user?.is_active}
                            width={21}
                            isActive={
                              userOnlineStatus?.[caseSummary?.accepted_date_user?.id]?.isOnline ??
                              false
                            }
                          />
                        </div>
                      )}
                      {/* )} */}
                    </div>

                    {/* New Entries */}
                    <div className="d-flex align-items-center mb-3 m-b-5 mx-auto">
                      <div className="col-md-3 text-start p-l-5 pe-0">
                        <div className="height-25 d-flex align-items-center">
                          <label
                            className="d-inline-block m-0 "
                            style={{
                              color: "var(--primary-25)",
                              fontWeight: "600",
                            }}
                            htmlFor="case_dropped"
                          >
                            Dropped
                          </label>
                        </div>
                      </div>
                      <div className="flex-g-1 p-l-5 p-r-5">
                        <input
                          type="date"
                          readOnly={isIntakePreview}
                          disabled={isIntakePreview}
                          id="case_dropped"
                          className="form-control height-25 p-0 p-l-5 p-r-5"
                          min="1000-01-01"
                          max="9999-12-31"
                          name="caseDates.case_dropped"
                          value={formik.values.caseDates.case_dropped}
                          onChange={formik.handleChange}
                        />
                      </div>
                      {/* {caseSummary?.incident_date_user && ( */}
                      {!isIntakePreview && (
                        <div className="p-r-5 p-l-5 d-flex align-items-center">
                          <UserChatBubble
                            userId={caseSummary?.case_dropped_user?.id}
                            first_name={caseSummary?.case_dropped_user?.first_name ?? ""}
                            last_name={caseSummary?.case_dropped_user?.last_name ?? ""}
                            profilePic={caseSummary?.case_dropped_user?.profile_pic ?? ""}
                            is_active={caseSummary?.case_dropped_user?.is_active}
                            width={21}
                            isActive={
                              userOnlineStatus?.[caseSummary?.case_dropped_user?.id]?.isOnline ??
                              false
                            }
                          />
                        </div>
                      )}
                      {/* )} */}
                    </div>
                    <div className="d-flex align-items-center mb-3 m-b-5 mx-auto">
                      <div className="col-md-3 text-start p-l-5 pe-0">
                        <div className="height-25 d-flex align-items-center">
                          <label
                            className="d-inline-block m-0 "
                            style={{
                              color: "var(--primary-25)",
                              fontWeight: "600",
                            }}
                            htmlFor="case_referred"
                          >
                            Referred
                          </label>
                        </div>
                      </div>
                      <div className="flex-g-1 p-l-5 p-r-5">
                        <input
                          type="date"
                          readOnly={isIntakePreview}
                          disabled={isIntakePreview}
                          id="case_referred"
                          className="form-control height-25 p-0 p-l-5 p-r-5"
                          min="1000-01-01"
                          max="9999-12-31"
                          name="caseDates.case_referred"
                          value={formik.values.caseDates.case_referred}
                          onChange={formik.handleChange}
                        />
                      </div>
                      {/* {caseSummary?.incident_date_user && ( */}
                      {!isIntakePreview && (
                        <div className="p-r-5 p-l-5 d-flex align-items-center">
                          <UserChatBubble
                            userId={caseSummary?.case_referred_user?.id}
                            first_name={caseSummary?.case_referred_user?.first_name ?? ""}
                            last_name={caseSummary?.case_referred_user?.last_name ?? ""}
                            profilePic={caseSummary?.case_referred_user?.profile_pic ?? ""}
                            is_active={caseSummary?.case_referred_user?.is_active}
                            width={21}
                            isActive={
                              userOnlineStatus?.[caseSummary?.case_referred_user?.id]?.isOnline ??
                              false
                            }
                          />
                        </div>
                      )}
                      {/* )} */}
                    </div>
                    <div className="d-flex align-items-center mb-3 m-b-5 mx-auto">
                      <div className="col-md-3 text-start p-l-5 pe-0">
                        <div className="height-25 d-flex align-items-center">
                          <label
                            className="d-inline-block m-0 "
                            style={{
                              color: "var(--primary-25)",
                              fontWeight: "600",
                            }}
                            htmlFor="case_rejected"
                          >
                            Rejected
                          </label>
                        </div>
                      </div>
                      <div className="flex-g-1 p-l-5 p-r-5">
                        <input
                          type="date"
                          readOnly={isIntakePreview}
                          disabled={isIntakePreview}
                          id="case_rejected"
                          className="form-control height-25 p-0 p-l-5 p-r-5"
                          min="1000-01-01"
                          max="9999-12-31"
                          name="caseDates.case_rejected"
                          value={formik.values.caseDates.case_rejected}
                          onChange={formik.handleChange}
                        />
                      </div>
                      {/* {caseSummary?.incident_date_user && ( */}
                      {!isIntakePreview && (
                        <div className="p-r-5 p-l-5 d-flex align-items-center">
                          <UserChatBubble
                            userId={caseSummary?.case_rejected_user?.id}
                            first_name={caseSummary?.case_rejected_user?.first_name ?? ""}
                            last_name={caseSummary?.case_rejected_user?.last_name ?? ""}
                            profilePic={caseSummary?.case_rejected_user?.profile_pic ?? ""}
                            is_active={caseSummary?.case_rejected_user?.is_active}
                            width={21}
                            isActive={
                              userOnlineStatus?.[caseSummary?.case_rejected_user?.id]?.isOnline ??
                              false
                            }
                          />
                        </div>
                      )}
                      {/* )} */}
                    </div>

                    {/* End of New */}
                    <div className="d-flex align-items-center mb-3 m-b-5 mx-auto">
                      <div className="col-md-3 p-l-5 pe-0 text-start">
                        <label
                          className="d-inline-block m-0 height-25"
                          style={{
                            color: "var(--primary-25)",
                            fontWeight: "600",
                          }}
                          htmlFor="date_closed"
                        >
                          Closed
                        </label>
                      </div>
                      <div className="flex-g-1 p-l-5 p-r-5">
                        <input
                          type="date"
                          readOnly={isIntakePreview}
                          disabled={isIntakePreview}
                          id="date_closed"
                          className="form-control height-25 p-0 p-l-5 p-r-5"
                          min="1000-01-01"
                          max="9999-12-31"
                          name="caseDates.date_closed"
                          value={formik.values.caseDates.date_closed}
                          onChange={formik.handleChange}
                        />
                      </div>
                      {/* {caseSummary?.date_closed_user && ( */}
                      {!isIntakePreview && (
                        <div className="p-r-5 p-l-5 d-flex align-items-center">
                          <UserChatBubble
                            userId={caseSummary?.date_closed_user?.id}
                            first_name={caseSummary?.date_closed_user?.first_name ?? ""}
                            last_name={caseSummary?.date_closed_user?.last_name ?? ""}
                            profilePic={caseSummary?.date_closed_user?.profile_pic ?? ""}
                            is_active={caseSummary?.date_closed_user?.is_active}
                            width={21}
                            isActive={
                              userOnlineStatus?.[caseSummary?.date_closed_user?.id]?.isOnline ??
                              false
                            }
                          />
                        </div>
                      )}
                    </div>
                  </form>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </div>
        </div>

        <div className="p-0 mt-0 padding-outside-btn-new-provider modal-footer">
          <button
            type="button"
            className="button-padding-footer-new-provider btn btn-secondary d-flex align-items-center justify-content-center "
            onClick={handleClose}
            disabled={isEnteringCase}
          >
            Cancel
          </button>
          <button
            form="edit_case_date_form"
            type="button"
            className="button-padding-footer-new-provider btn btn-success d-flex align-items-center justify-content-center "
            onClick={isIntakePreview ? onEnterCase : formik.handleSubmit}
            disabled={
              isIntakePreview
                ? isEnteringCase || !intakeCase.client_id || !intakeCase.case_id
                : isLoading
            }
          >
            {isIntakePreview
              ? isEnteringCase
                ? "Enter Case"
                : "Enter Case"
              : isLoading
                ? "Saving..."
                : "Save and Close"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
